import { initializePaddle, type Paddle, type PricePreviewParams } from '@paddle/paddle-js';
import { paddleCountryCode } from '../../../shared/paddle-country';
import type { PublicCommerce } from './commerce';

type Configuration = Pick<PublicCommerce, 'paddleEnvironment' | 'paddleClientToken'>;
let initialized: { key: string; value: Promise<Paddle> } | undefined;

export function configuredPaddle(config: Configuration): Promise<Paddle> {
  const environment = config.paddleEnvironment;
  const token = config.paddleClientToken;
  if (environment !== 'sandbox' && environment !== 'live') return Promise.reject(new Error('Paddle environment is not configured.'));
  if (!token || !token.startsWith(environment === 'sandbox' ? 'test_' : 'live_')) return Promise.reject(new Error('Paddle checkout token does not match its environment.'));
  const key = `${environment}:${token}`;
  if (initialized && initialized.key !== key) return Promise.reject(new Error('Paddle configuration changed. Reload this page before checkout.'));
  if (!initialized) {
    const value = initializePaddle({ token, environment: environment === 'live' ? 'production' : 'sandbox' }).then(paddle => {
      if (!paddle) throw new Error('The payment form could not load.');
      return paddle;
    }).catch(error => { initialized = undefined; throw error; });
    initialized = { key, value };
  }
  return initialized.value;
}

export interface LocalizedPrices { month: string; year: string; currency: string }

export async function previewPersonalPrices(paddle: Pick<Paddle, 'PricePreview'>, priceId: { month: string; year: string }, country?: string): Promise<LocalizedPrices> {
  if (!priceId.month || !priceId.year || priceId.month === priceId.year) throw new Error('Personal billing prices are not configured.');
  const code = paddleCountryCode(country);
  const request: PricePreviewParams = { items: [{ priceId: priceId.month, quantity: 1 }, { priceId: priceId.year, quantity: 1 }], ...(code ? { address: { countryCode: code } } : {}) };
  const result = await paddle.PricePreview(request);
  const prices = new Map(result.data.details.lineItems.map(item => [item.price.id, item.formattedTotals.subtotal]));
  const month = prices.get(priceId.month);
  const year = prices.get(priceId.year);
  if (!month || !year) throw new Error('Paddle did not return both Personal billing prices.');
  return { month, year, currency: result.data.currencyCode };
}
